import { useEffect, useState } from "react";
import { Check as CheckIcon, Coins as CoinsIcon, Receipt as ReceiptIcon } from "@tamagui/lucide-icons";
import { Badge, Body1, Body2, Body3, Card, Loader, presenceTransition, ResponsiveImage, Subtitle1, TextSpan, useAnimatedEllipsis, XStack, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
export type SaveEducationOutcomes = {
  continue: {};
  back: {};
};
const Outcome = tagged<SaveEducationOutcomes>();
const CLAIMS = [{
  provider: "Houston Methodist",
  amount: "$2,300",
  flag: "Overcharge risk",
  status: "warning"
}, {
  provider: "Methodist Imaging",
  amount: "$640",
  flag: "Duplicate charges",
  status: "negative"
}, {
  provider: "BlueStar Physical Therapy",
  amount: "$930",
  flag: "Coverage issue",
  status: "warning"
}] as const;

// Simulated scan pacing: the first verdict lands fast enough to read as live,
// and the stagger keeps rows resolving one at a time. Exported for tests.
export const SCAN_FIRST_RESULT_MS = 1400;
export const SCAN_RESULT_STAGGER_MS = 700;
function ClaimRow({
  provider,
  amount,
  flag,
  status,
  resolved
}: {
  provider: string;
  amount: string;
  flag: string;
  status: "warning" | "negative";
  resolved: boolean;
}) {
  return <XStack alignItems="center" justifyContent="space-between" gap="$sp1.5" paddingHorizontal="$sp1.5" paddingVertical="$sp1" borderRadius="$br2" borderWidth={0.5} borderColor="$borderDefault" backgroundColor="$surface1" data-sentry-element="XStack" data-sentry-component="ClaimRow" data-sentry-source-file="SaveEducation.tsx">
      <YStack flexShrink={1} gap="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="SaveEducation.tsx">
        <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="SaveEducation.tsx">{provider}</Body3>
        <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="SaveEducation.tsx">{amount}</Subtitle1>
      </YStack>
      {resolved ? <XStack transition={presenceTransition} enterStyle={{
      opacity: 0,
      scale: 0.96
    }} opacity={1} scale={1}>
          <Badge status={status}>{flag}</Badge>
        </XStack> : <Loader size="$iconSmall" color="$iconSubtle" />}
    </XStack>;
}

// Brand-filled "scan complete" circle per design ("Icon container" with the
// Brand button fill and a plain check inside).
function ScanCompleteCheck() {
  return <XStack width="$si3" height="$si3" alignItems="center" justifyContent="center" borderRadius="$brFull" backgroundColor="$bgBrandStrong" transition={presenceTransition} enterStyle={{
    opacity: 0,
    scale: 0.96
  }} opacity={1} scale={1} data-sentry-element="XStack" data-sentry-component="ScanCompleteCheck" data-sentry-source-file="SaveEducation.tsx">
      <CheckIcon size="$iconSmall" color="$iconDefault" data-sentry-element="CheckIcon" data-sentry-source-file="SaveEducation.tsx" />
    </XStack>;
}
function StatChip({
  children
}: {
  children: React.ReactNode;
}) {
  return <XStack width="$si4" height="$si4" alignItems="center" justifyContent="center" borderRadius="$brFull" backgroundColor="$surface0" data-sentry-element="XStack" data-sentry-component="StatChip" data-sentry-source-file="SaveEducation.tsx">
      {children}
    </XStack>;
}

/** Save-branch interstitial with a simulated claim scan; no persistence. */
export function SaveEducation({
  onDone
}: {
  onDone: OnDone<SaveEducationOutcomes>;
}) {
  const [resolvedCount, setResolvedCount] = useState(0);
  const scanComplete = resolvedCount >= CLAIMS.length;
  const ellipsis = useAnimatedEllipsis(!scanComplete);
  useEffect(() => {
    const timers = CLAIMS.map((_, index) => setTimeout(() => setResolvedCount(count => Math.max(count, index + 1)), SCAN_FIRST_RESULT_MS + index * SCAN_RESULT_STAGGER_MS));
    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    };
  }, []);
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="SaveEducation" data-sentry-source-file="SaveEducation.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="SaveEducation.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="SaveEducation.tsx">
        <OnboardingScreen.Header alignment="left" title="We'll help you save on future bills" subtitle="Connect your insurance and we'll monitor every claim — flagging issues before they become bills." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="SaveEducation.tsx" />
        <YStack gap="$sp3" data-sentry-element="YStack" data-sentry-source-file="SaveEducation.tsx">
          <Card variant="elevated" size="large" data-sentry-element="Card" data-sentry-source-file="SaveEducation.tsx">
            <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="SaveEducation.tsx">
              <XStack alignItems="center" gap="$sp1" paddingVertical="$sp1" paddingRight="$sp1.5" data-sentry-element="XStack" data-sentry-source-file="SaveEducation.tsx">
                <ResponsiveImage alt="Your insurance plan" src={{
                web: "/static/onboarding/insurance-avatar.png",
                native: require("../../assets/onboarding/insurance-avatar.png")
              }} objectFit="contain" height={48} width={48} data-sentry-element="ResponsiveImage" data-sentry-source-file="SaveEducation.tsx" />
                <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="SaveEducation.tsx">
                  <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="SaveEducation.tsx">Your Insurance</Subtitle1>
                  <Body2 color="$textDefault" data-sentry-element="Body2" data-sentry-source-file="SaveEducation.tsx">
                    {scanComplete ? `Found ${CLAIMS.length} claims` : `Scanning${ellipsis}`}
                  </Body2>
                </YStack>
                {scanComplete ? <ScanCompleteCheck /> : <Loader size="$iconSmall" color="$iconSubtle" />}
              </XStack>
              {CLAIMS.map((claim, index) => <ClaimRow key={claim.provider} provider={claim.provider} amount={claim.amount} flag={claim.flag} status={claim.status} resolved={index < resolvedCount} />)}
            </YStack>
          </Card>
          <YStack gap="$sp1.5" data-sentry-element="YStack" data-sentry-source-file="SaveEducation.tsx">
            <XStack gap="$sp1.5" alignItems="center" data-sentry-element="XStack" data-sentry-source-file="SaveEducation.tsx">
              <StatChip data-sentry-element="StatChip" data-sentry-source-file="SaveEducation.tsx">
                <ReceiptIcon size="$iconSmall" color="$iconDefault" data-sentry-element="ReceiptIcon" data-sentry-source-file="SaveEducation.tsx" />
              </StatChip>
              <Body1 flexShrink={1} data-sentry-element="Body1" data-sentry-source-file="SaveEducation.tsx">
                <TextSpan bold data-sentry-element="TextSpan" data-sentry-source-file="SaveEducation.tsx">8 in 10 medical bills</TextSpan> contain billing
                errors
              </Body1>
            </XStack>
            <XStack gap="$sp1.5" alignItems="center" data-sentry-element="XStack" data-sentry-source-file="SaveEducation.tsx">
              <StatChip data-sentry-element="StatChip" data-sentry-source-file="SaveEducation.tsx">
                <CoinsIcon size="$iconSmall" color="$iconDefault" data-sentry-element="CoinsIcon" data-sentry-source-file="SaveEducation.tsx" />
              </StatChip>
              <Body1 flexShrink={1} data-sentry-element="Body1" data-sentry-source-file="SaveEducation.tsx">
                Granted users <TextSpan bold data-sentry-element="TextSpan" data-sentry-source-file="SaveEducation.tsx">save an average of $840</TextSpan>
              </Body1>
            </XStack>
          </YStack>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="SaveEducation.tsx">
        <OnboardingScreen.PrimaryAction testID="save-education.continue" variant="brand" onPress={() => onDone(Outcome.continue())} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="SaveEducation.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}